import {
  Calendar,
  FileText,
  Tag,
  User,
  X,
} from "lucide-react"
import { useEffect, useState } from "react"
import type { Task } from "../types"
import TaskCommentsSection from "./TaskCommentsSection"
import SubtaskChecklist from "./SubtaskChecklist"
import TaskTimeLog from "./TaskTimeLog"

type TaskDetailModalProps = {
  task: Task | null
  isOpen: boolean
  onClose: () => void
  onUpdate: (taskId: string, updates: Partial<Task>) => void
  onDelete?: (taskId: string) => void
}

const statusOptions: Array<{ value: Task["status"]; label: string }> = [
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "review", label: "Review" },
  { value: "done", label: "Done" },
]

const priorityOptions: Array<{ value: Task["priority"]; label: string; color: string }> = [
  { value: "low", label: "Low", color: "bg-slate-100 text-slate-700" },
  { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-700" },
  { value: "high", label: "High", color: "bg-rose-100 text-rose-700" },
]

const TaskDetailModal = ({
  task,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
}: TaskDetailModalProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedTask, setEditedTask] = useState<Partial<Task> | null>(null)
  const [newTag, setNewTag] = useState("")
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([])

  useEffect(() => {
    if (isOpen) {
      fetch("/api/users").then(r => r.json()).then(d => {
        if (d.users) setUsers(d.users)
      }).catch(() => {})
    }
  }, [isOpen])

  if (!isOpen || !task) return null

  const taskToDisplay = editedTask ? { ...task, ...editedTask } : task

  const handleSave = () => {
    if (editedTask) {
      onUpdate(task.id, editedTask)
      setIsEditing(false)
      setEditedTask(null)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditedTask(null)
  }

  const handleFieldChange = (field: keyof Task, value: unknown) => {
    setEditedTask((prev) => ({ ...prev, [field]: value }))
    setIsEditing(true)
  }

  // Immediate save for quick fields (status, priority, assignee)
  const handleQuickUpdate = (updates: Partial<Task>) => {
    onUpdate(task.id, updates)
  }

  const handleAddTag = () => {
    if (newTag.trim() && !taskToDisplay.tags.includes(newTag.trim())) {
      handleFieldChange("tags", [...taskToDisplay.tags, newTag.trim()])
      setNewTag("")
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    handleFieldChange(
      "tags",
      taskToDisplay.tags.filter((tag) => tag !== tagToRemove),
    )
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "No due date"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-end bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-white p-6">
          <div className="flex-1">
            {isEditing ? (
              <input
                type="text"
                value={editedTask?.title ?? taskToDisplay.title}
                onChange={(e) => handleFieldChange("title", e.target.value)}
                className="w-full text-xl font-semibold text-slate-900 outline-none"
                autoFocus
              />
            ) : (
              <h2 className="text-xl font-semibold text-slate-900">{taskToDisplay.title}</h2>
            )}
            {taskToDisplay.project && (
              <p className="mt-1 text-sm text-stone">
                {taskToDisplay.project}
                {taskToDisplay.client && ` · ${taskToDisplay.client}`}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-4 flex h-8 w-8 items-center justify-center rounded-full text-stone hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Status and Priority — always interactive */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-xs font-medium text-slate-700">Status</label>
                <select
                  value={taskToDisplay.status}
                  onChange={(e) => handleQuickUpdate({ status: e.target.value as Task["status"] })}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-xs font-medium text-slate-700">Priority</label>
                <select
                  value={taskToDisplay.priority}
                  onChange={(e) => handleQuickUpdate({ priority: e.target.value as Task["priority"] })}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                >
                  {priorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Assignee — always interactive */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-700">
                <User className="h-4 w-4" />
                Assignee
              </label>
              <select
                value={taskToDisplay.assigneeId ?? ""}
                onChange={(e) => {
                  const selectedUser = users.find(u => u.id === e.target.value)
                  handleQuickUpdate({
                    assigneeId: e.target.value || undefined,
                    assignee: selectedUser?.name || undefined,
                  })
                }}
                className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
              >
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="mb-2 block text-xs font-medium text-slate-700">Description</label>
              {isEditing ? (
                <textarea
                  value={editedTask?.description ?? taskToDisplay.description ?? ""}
                  onChange={(e) => handleFieldChange("description", e.target.value)}
                  className="w-full resize-none rounded-lg border border-line bg-white p-3 text-sm outline-none focus:border-moss"
                  rows={4}
                  placeholder="Add a description..."
                />
              ) : (
                <p
                  className="cursor-pointer rounded-lg border border-line bg-slate-50 p-3 text-sm text-slate-700"
                  onClick={() => setIsEditing(true)}
                >
                  {taskToDisplay.description || "No description — click to edit"}
                </p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-700">
                <Calendar className="h-4 w-4" />
                Due Date
              </label>
              {isEditing ? (
                <input
                  type="date"
                  value={
                    editedTask?.dueDate
                      ? new Date(editedTask.dueDate).toISOString().split("T")[0]
                      : taskToDisplay.dueDate
                        ? new Date(taskToDisplay.dueDate).toISOString().split("T")[0]
                        : ""
                  }
                  onChange={(e) =>
                    handleFieldChange("dueDate", e.target.value ? new Date(e.target.value).toISOString() : undefined)
                  }
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                />
              ) : (
                <p
                  className="cursor-pointer text-sm text-slate-700"
                  onClick={() => setIsEditing(true)}
                >
                  {formatDate(taskToDisplay.dueDate)}
                </p>
              )}
            </div>

            {/* Subtasks */}
            <div>
              <SubtaskChecklist taskId={task.id} />
            </div>

            {/* Time */}
            <div>
              <TaskTimeLog taskId={task.id} />
            </div>

            {/* Tags */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-700">
                <Tag className="h-4 w-4" />
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {taskToDisplay.tags.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1.5 rounded-full bg-moss/10 px-3 py-1 text-xs font-medium text-moss"
                  >
                    {tag}
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-moss/70"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}
                {isEditing && (
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          handleAddTag()
                        }
                      }}
                      placeholder="Add tag"
                      className="w-24 rounded-full border border-line bg-white px-2 py-1 text-xs outline-none focus:border-moss"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="rounded-full bg-moss px-2 py-1 text-xs text-white hover:bg-moss/90"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Comments & Activity */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-700">
                <FileText className="h-4 w-4" />
                Comments & Activity
              </label>
              <TaskCommentsSection taskId={task.id} />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="mt-6 flex items-center justify-between border-t border-line pt-6">
            <div>
              {onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm("Are you sure you want to delete this task?")) {
                      onDelete(task.id)
                      onClose()
                    }
                  }}
                  className="rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                >
                  Delete Task
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="rounded-lg bg-moss px-4 py-2 text-sm font-medium text-white hover:bg-moss/90"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="rounded-lg bg-moss px-4 py-2 text-sm font-medium text-white hover:bg-moss/90"
                >
                  Edit Task
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TaskDetailModal
